/**
 * Datasets API Client
 *
 * API client functions for all Dataset management endpoints.
 * Extends BaseApiClient for authentication, error handling, and type-safe requests.
 */

import { getConfig } from '@/config/runtimeConfig';

import { BaseApiClient } from './baseApiClient';

import type { DatasetUsageCommonEndpoint, DatasetUsagePeriod } from '@gw/ui';
import type {
  Dataset,
  CreateDatasetRequest,
  UpdateDatasetRequest,
  ListDatasetsParams,
  ListDatasetsResponse,
  QueryDatasetRequest,
  QueryDatasetResponse,
  DatasetCredentialsPayload,
  DatasetOpenApiSpec,
  TestQueryRequest,
  TestQueryResponse,
  QueryExportResponse,
  SubmitQueryRequest,
  SubmitQueryResponse,
  ListQueriesParams,
  ListQueriesResponse,
  DatasetQuery,
  DatasetQueryResponse,
  QueryTokens,
  QueryResult,
  QueryDelivery,
  QueryDeliveryProof,
  QueryNotification,
  SchemaField,
  QueryProxyConfig,
} from '@gw/ui/types';

/**
 * A single credential to forward to the merged server-side test-query proxy
 * (POST /v1/datasets/test-query, GW-11216). Currently only an api_key placed in
 * the query string is carried this way — header/basic/bearer credentials are baked
 * into `headers` by the caller. Not yet published by @gw/ui@6.2.0, defined locally.
 */
export interface CollectionTestQueryCredential {
  name?: string;
  value?: string;
  location?: 'header' | 'query';
}

/**
 * Request body for the merged server-side proxy that tests an UNSAVED dataset's
 * endpoint during create-mode wizard authoring (POST /v1/datasets/test-query,
 * GW-11216). The endpoint path (with path params substituted and query string
 * appended) and headers are assembled client-side; the proxy performs the outbound
 * request server-side so provider credentials never leak into the browser network log.
 * Not yet published by @gw/ui@6.2.0, defined locally.
 *
 * The `credential` union is structurally discriminated by `authScheme`:
 * `DatasetCredentialsPayload` always carries it (required field); `CollectionTestQueryCredential`
 * never does. Callers can narrow at runtime with `'authScheme' in credential`.
 */
export interface CollectionTestQueryRequest {
  baseUrl: string;
  endpoint: string;
  method?: string;
  parameters?: Record<string, unknown>;
  headers?: Record<string, string>;
  body?: string;
  credential?: CollectionTestQueryCredential | DatasetCredentialsPayload;
}

/**
 * TOS (Terms of Service) types — GW-11440/GW-11453, PRP-MKTPL-016 FR-6/FR-7.
 *
 * Mirrors the canonical userAcceptance/orgAcceptance shape shared across all
 * four marketplace item types (applications/missions/service-offerings/
 * datasets) — see gw-datasets-api's `functions/datasets/handler/tos.go`
 * (`TOSAcceptanceStatusResponse`/`TOSAcceptanceDetail`/`TOSAcceptResponse`)
 * for the backend reference this shape is drawn from. Defined locally since
 * these fields are not yet part of `@gw/ui/types` (that package's
 * `TOSAcceptanceStatus` is the older flat-boolean shape used by
 * Application TOS, pending its own migration).
 */
export type DatasetTOSRequiredLevel = 'none' | 'user' | 'org' | 'both';

export interface DatasetTOSAcceptanceDetail {
  accepted: boolean;
  acceptanceType: 'user' | 'org';
  acceptedVersion: number;
  acceptedAt: string;
}

/**
 * GET /datasets/{datasetId}/tos/acceptance response.
 */
export interface DatasetTOSAcceptanceStatus {
  itemId: string;
  tosRequiredLevel: DatasetTOSRequiredLevel;
  tosVersion: number | null;
  userAcceptance: DatasetTOSAcceptanceDetail | null;
  orgAcceptance: DatasetTOSAcceptanceDetail | null;
  userNeedsReaccept: boolean;
  orgNeedsReaccept: boolean;
  canProceed: boolean;
}

/**
 * POST /datasets/{datasetId}/tos/accept/{user,org} response.
 */
export interface DatasetTOSAcceptResponse {
  acceptanceType: 'user' | 'org';
  tosVersion: number;
  acceptedAt: string;
}

/**
 * GW-11493: FR-29 cross-dataset org TOS overview.
 * GET /datasets/tos/org-overview (org_admin/global_admin only).
 * Mirrors the shape gw-datasets-api's `TOSOrgOverviewResponse` returns
 * (`functions/datasets/handler/tos.go`) — the same summary/items envelope
 * as applications' `ApplicationOrgTOSOverviewResponse`, but with
 * itemId/itemName rather than appId/appName/category/iconUrl (datasets have
 * no marketplace category/icon on this read).
 */
export interface DatasetOrgTOSOverviewResponse {
  items: Array<{
    itemId: string;
    itemName: string;
    tosRequiredLevel: DatasetTOSRequiredLevel;
    tosVersion: number | null;
    orgAccepted: boolean;
    orgNeedsReaccept: boolean;
  }>;
  summary: {
    total: number;
    accepted: number;
    pending: number;
    needsReaccept: number;
  };
}

/**
 * Forward-compatible superset of the payload returned by GET /v1/queries/{id}.
 *
 * The provider currently emits the flat `DatasetQueryResponse` wire shape
 * (queryId, flat tokensReserved/tokensCost, flat recordCount/ssdArtifactId, and a
 * flat `error` string) with NO nested `tokens`/`result` objects and NO `id`. The
 * optional nested fields below are tolerated so that a future provider emitting the
 * internal `DatasetQuery` shape directly still normalizes cleanly.
 */
type RawQueryPayload = DatasetQueryResponse & {
  id?: string;
  tokens?: Partial<QueryTokens>;
  tokensRefunded?: number;
  refunded?: number;
  result?: Partial<QueryResult>;
  dataSize?: number;
  executionTimeMs?: number;
  deliveryProof?: QueryDeliveryProof;
  delivery?: QueryDelivery;
  notification?: QueryNotification;
  expiresAt?: string;
};

/**
 * Normalize the wire payload from GET /v1/queries/{id} into the full `DatasetQuery`
 * contract that `@gw/ui`'s `QueryResultPage` requires (GW-13124).
 *
 * `QueryResultPage` dereferences `query.tokens.{reserved,final,refunded}`,
 * `query.result.{recordCount,ssdArtifactId,deliveryProof,dataSize,executionTimeMs,error}`,
 * `query.id`, `query.endpoint`, `query.parameters`, and `query.notification` WITHOUT
 * guards. The wire shape has none of the nested objects, so a bare cast leaves them
 * `undefined` and the page crashes on load (`Cannot read properties of undefined`).
 * This maps/populates every field the page reads so it cannot crash on a missing one.
 */
export function mapQueryResponseToDatasetQuery(raw: RawQueryPayload): DatasetQuery {
  const reserved = raw.tokens?.reserved ?? raw.tokensReserved ?? 0;
  const final = raw.tokens?.final ?? raw.tokensCost ?? raw.tokensReserved ?? 0;
  const refunded = raw.tokens?.refunded ?? raw.tokensRefunded ?? raw.refunded ?? 0;

  // The wire `error` is a plain string; QueryResultPage renders result.error.code and
  // result.error.message. Preserve a structured error if the provider ever sends one.
  const error =
    raw.result?.error ??
    (typeof raw.error === 'string' && raw.error.length > 0
      ? { code: 'QUERY_ERROR', message: raw.error }
      : undefined);

  const result: QueryResult = {
    recordCount: raw.result?.recordCount ?? raw.recordCount,
    dataSize: raw.result?.dataSize ?? raw.dataSize,
    ssdArtifactId: raw.result?.ssdArtifactId ?? raw.ssdArtifactId,
    deliveryProof: raw.result?.deliveryProof ?? raw.deliveryProof,
    executionTimeMs: raw.result?.executionTimeMs ?? raw.executionTimeMs,
    error,
  };

  return {
    id: raw.id ?? raw.queryId,
    datasetId: raw.datasetId,
    datasetName: raw.datasetName,
    orgId: raw.orgId,
    userId: raw.userId,
    endpoint: raw.endpoint,
    parameters: raw.parameters ?? {},
    delivery: raw.delivery,
    notification: raw.notification ?? { method: 'none' },
    status: raw.status,
    tokens: { reserved, final, refunded },
    result,
    submittedAt: raw.submittedAt,
    completedAt: raw.completedAt,
    expiresAt: raw.expiresAt,
    results: raw.results,
  };
}

class DatasetsApiClient extends BaseApiClient {
  constructor() {
    // GW-6813: Dataset endpoints live on gw-datasets-api (datasets.{env}.generalwisdom.com),
    // separate from gw-functions. Falls back to apiBaseUrl when datasetsApiBaseUrl is not configured.
    // GW-6394: Use baseURLFactory to defer getConfig() until first API call.
    // GW-8262: suppressSessionExpired — dataset lookups/queries return 401/404
    // for a bad dataset ID or access-denied resource state, not for session
    // expiry. Treating any 401 from this client as "session invalid" force-
    // logged users out when they hit a bad dataset link. Genuine session
    // expiry is caught upstream by devStackAuth.util's token-refresh path.
    super({
      baseURLFactory: () => `${getConfig().datasetsApiBaseUrl ?? getConfig().apiBaseUrl}/v1`,
      suppressSessionExpired: true,
    });
  }

  // ============================================================================
  // Dataset Management APIs
  // ============================================================================

  /**
   * Get all datasets for the current organization
   * GET /datasets
   *
   * @returns Promise<Dataset[]> - Array of datasets
   * @throws {ApiError} If the API request fails
   *
   * @example
   * ```typescript
   * const datasets = await datasetsApi.getDatasets();
   * console.debug('Datasets:', datasets);
   * ```
   */
  async getDatasets(): Promise<Dataset[]> {
    const response = await this.get<{ datasets: Dataset[] }>('/datasets');
    return response.datasets;
  }

  /**
   * Get a single dataset by ID
   * GET /datasets/{datasetId}
   *
   * @param datasetId - The unique identifier of the dataset
   * @returns Promise<Dataset> - The requested dataset
   * @throws {ApiError} If the dataset is not found or request fails
   *
   * @example
   * ```typescript
   * const dataset = await datasetsApi.getDataset('dataset-123');
   * console.debug('Dataset:', dataset.name);
   * ```
   */
  async getDataset(datasetId: string): Promise<Dataset> {
    return this.get<Dataset>(`/datasets/${datasetId}`);
  }

  /**
   * Get a single dataset by its URL slug
   * GET /datasets/by-slug/{slug}
   *
   * GW-13274: authenticated, org-scoped slug lookup. The authed marketplace
   * dataset detail page uses this to resolve a slug URL directly on the JWT
   * client, eliminating the prior extra round-trip to the unauthenticated
   * public endpoint (GET /public/datasets/{slug}) that existed only to
   * translate a slug into a UUID. The response is the identical `Dataset`
   * schema `getDataset` returns and runs the same access-control gate
   * (a private dataset of another org resolves via the slug then returns 403).
   *
   * @param slug - The URL slug of the dataset
   * @returns Promise<Dataset> - The requested dataset
   * @throws {ApiError} If the dataset is not found (404) or access is denied (403)
   *
   * @example
   * ```typescript
   * const dataset = await datasetsApi.getDatasetBySlug('logiover_binance-spot-market-scraper');
   * console.debug('Dataset:', dataset.name);
   * ```
   */
  async getDatasetBySlug(slug: string): Promise<Dataset> {
    return this.get<Dataset>(`/datasets/by-slug/${encodeURIComponent(slug)}`);
  }

  /**
   * Create a new dataset
   * POST /datasets
   *
   * @param dataset - The dataset creation request payload
   * @returns Promise<Dataset> - The created dataset with generated ID
   * @throws {ApiError} If the creation fails
   *
   * @example
   * ```typescript
   * const newDataset = await datasetsApi.createDataset({
   *   name: 'My Dataset',
   *   description: 'Dataset description',
   *   visibility: 'public',
   *   authScheme: 'none',
   * });
   * ```
   */
  async createDataset(dataset: CreateDatasetRequest): Promise<Dataset> {
    return this.post<Dataset>('/datasets', dataset);
  }

  /**
   * Update an existing dataset
   * PUT /datasets/{datasetId}
   *
   * @param datasetId - The unique identifier of the dataset to update
   * @param updates - Partial dataset object with fields to update
   * @returns Promise<Dataset> - The updated dataset
   * @throws {ApiError} If the dataset is not found or update fails
   *
   * @example
   * ```typescript
   * const updated = await datasetsApi.updateDataset('dataset-123', {
   *   name: 'Updated Name',
   *   description: 'Updated description',
   *   status: 'inactive'
   * });
   * ```
   */
  async updateDataset(datasetId: string, updates: UpdateDatasetRequest): Promise<Dataset> {
    return this.put<Dataset>(`/datasets/${datasetId}`, updates);
  }

  /**
   * Delete a dataset
   * DELETE /datasets/{datasetId}
   *
   * @param datasetId - The unique identifier of the dataset to delete
   * @returns Promise<void> - Resolves when deletion is successful
   * @throws {ApiError} If the dataset is not found or deletion fails
   *
   * @example
   * ```typescript
   * await datasetsApi.deleteDataset('dataset-123');
   * console.debug('Dataset deleted successfully');
   * ```
   */
  async deleteDataset(datasetId: string): Promise<void> {
    return this.delete<void>(`/datasets/${datasetId}`);
  }

  // ============================================================================
  // Extended Dataset APIs (GW-1216, GW-1243)
  // ============================================================================

  /**
   * List datasets with search and pagination
   * GET /datasets?search=&limit=&cursor=&status=&visibility=
   *
   * @param params - Optional search and pagination parameters
   * @returns Promise<ListDatasetsResponse> - Paginated list of datasets
   * @throws {ApiError} If the API request fails
   *
   * @example
   * ```typescript
   * const response = await datasetsApi.listDatasets({
   *   search: 'customer',
   *   limit: 10,
   *   status: 'active'
   * });
   * console.debug('Datasets:', response.datasets);
   * console.debug('Has more:', response.pagination.hasMore);
   * ```
   */
  async listDatasets(params?: ListDatasetsParams): Promise<ListDatasetsResponse> {
    const queryParams = params
      ? this.buildQueryString(params as Record<string, string | number | boolean | undefined>)
      : '';
    return this.get<ListDatasetsResponse>(`/datasets${queryParams}`);
  }

  /**
   * Execute a query against a dataset
   * POST /datasets/{datasetId}/query
   *
   * @param datasetId - The unique identifier of the dataset
   * @param request - Query execution parameters
   * @returns Promise<QueryDatasetResponse> - Query execution results
   * @throws {ApiError} If the query fails or dataset not found
   *
   * @example
   * ```typescript
   * const response = await datasetsApi.queryDataset('dataset-123', {
   *   query: 'SELECT * FROM customers WHERE region = :region',
   *   parameters: { region: 'US' },
   *   limit: 100
   * });
   * console.debug('Query results:', response.results);
   * ```
   */
  async queryDataset(
    datasetId: string,
    request: QueryDatasetRequest
  ): Promise<QueryDatasetResponse> {
    return this.post<QueryDatasetResponse>(`/datasets/${datasetId}/query`, request);
  }

  /**
   * Get the parsed OpenAPI specification for a dataset
   * GET /datasets/{datasetId}/openapi
   *
   * @param datasetId - The unique identifier of the dataset
   * @returns Promise<DatasetOpenApiSpec> - Parsed OpenAPI specification
   * @throws {ApiError} If the dataset is not found or has no spec
   *
   * @example
   * ```typescript
   * const spec = await datasetsApi.getOpenApiSpec('dataset-123');
   * console.debug('API version:', spec.info.version);
   * console.debug('Paths:', Object.keys(spec.paths));
   * ```
   */
  async getOpenApiSpec(datasetId: string): Promise<DatasetOpenApiSpec> {
    return this.get<DatasetOpenApiSpec>(`/datasets/${datasetId}/openapi.json`);
  }

  /**
   * Execute a test query against a dataset (admin only)
   * POST /datasets/{datasetId}/test-query
   *
   * Admin-only endpoint for validating OpenAPI configuration without incurring charges.
   *
   * @param datasetId - The unique identifier of the dataset
   * @param request - Optional test query parameters
   * @returns Promise<TestQueryResponse> - Test query results with execution plan
   * @throws {ApiError} If the test query fails or unauthorized
   *
   * @example
   * ```typescript
   * const response = await datasetsApi.testQuery('dataset-123', {
   *   endpoint: '/users',
   *   method: 'GET',
   *   parameters: { limit: '10' },
   * });
   * console.debug('Results:', response.results, 'Rows:', response.rowCount);
   * ```
   */
  async testQuery(datasetId: string, request: TestQueryRequest): Promise<TestQueryResponse> {
    return this.post<TestQueryResponse>(`/datasets/${datasetId}/test-query`, request);
  }

  /**
   * Execute a test query against an UNSAVED (in-wizard) dataset endpoint via the
   * merged server-side proxy.
   * POST /datasets/test-query
   *
   * GW-11217/GW-11216: create-mode wizard testing no longer issues a direct browser
   * fetch to the provider API (which would leak query-string credentials into the
   * network log and hit CORS). The proxy performs the outbound request server-side
   * using the assembled path/headers and the optional forwarded `credential`.
   *
   * @param request - Fully-assembled collection test-query request
   * @returns Promise<TestQueryResponse> - Test query results
   * @throws {ApiError} If the test query fails
   */
  async testUnsavedEndpointQuery(request: CollectionTestQueryRequest): Promise<TestQueryResponse> {
    return this.post<TestQueryResponse>('/datasets/test-query', request);
  }

  /**
   * Export query results via presigned URL
   * GET /queries/{queryId}/export?format=json|csv
   *
   * Returns a presigned S3 URL for downloading the query results.
   * The caller should redirect the browser to the returned URL to trigger the download.
   *
   * @param queryId - The unique identifier of the query
   * @param format - Export format: 'json' or 'csv'
   * @returns Promise<QueryExportResponse> - Presigned URL and metadata
   * @throws {ApiError} If the export fails or query not found
   *
   * @example
   * ```typescript
   * const exportData = await datasetsApi.exportQuery('query-456', 'json');
   * window.open(exportData.url, '_blank');
   * ```
   */
  async exportQuery(
    queryId: string,
    format: 'json' | 'csv' = 'json'
  ): Promise<QueryExportResponse> {
    const queryParams = this.buildQueryString({ format });
    return this.get<QueryExportResponse>(`/queries/${queryId}/export${queryParams}`);
  }

  /**
   * Estimate the token cost for a query
   * POST /datasets/{datasetId}/queries/estimate
   *
   * @param datasetId - The unique identifier of the dataset
   * @param request - Estimate request with operationId, parameters, maxRecords, deliveryMethod
   * @returns Promise with estimated tokens and records
   * @throws {ApiError} If estimation fails
   */
  async estimateQueryCost(
    datasetId: string,
    request: {
      operationId: string;
      parameters: Record<string, unknown>;
      maxRecords?: number;
      deliveryMethod?: string;
    }
  ): Promise<{ estimatedTokens: number; maxRecords: number }> {
    return this.post(`/datasets/${datasetId}/queries/estimate`, request);
  }

  /**
   * Submit a query to a dataset
   * POST /datasets/{datasetId}/queries
   *
   * @param datasetId - The unique identifier of the dataset
   * @param request - Query submission request matching backend SubmitQueryRequest shape
   * @returns Promise with query ID and status
   * @throws {ApiError} If submission fails
   */
  async submitQuery(datasetId: string, request: SubmitQueryRequest): Promise<SubmitQueryResponse> {
    return this.post(`/datasets/${datasetId}/queries`, request);
  }

  // ============================================================================
  // Per-Dataset Query APIs (GW-1862)
  // ============================================================================

  /**
   * Get queries for a specific dataset
   * GET /datasets/{datasetId}/queries
   *
   * @param datasetId - The unique identifier of the dataset
   * @param params - Optional pagination and filter parameters
   * @returns Promise with list of queries and pagination info
   * @throws {ApiError} If the API request fails
   */
  async getDatasetQueries(
    datasetId: string,
    params?: ListQueriesParams
  ): Promise<ListQueriesResponse> {
    const queryParams = params
      ? this.buildQueryString(params as Record<string, string | number | boolean | undefined>)
      : '';
    return this.get<ListQueriesResponse>(`/datasets/${datasetId}/queries${queryParams}`);
  }

  // ============================================================================
  // Query History APIs (GW-1459)
  // ============================================================================

  /**
   * Get all queries for the current organization
   * GET /queries
   *
   * Fetches query history across all datasets for the organization.
   *
   * @param params - Optional pagination and filter parameters
   * @returns Promise with list of queries and pagination info
   * @throws {ApiError} If the API request fails
   *
   * @example
   * ```typescript
   * const response = await datasetsApi.getAllQueries({
   *   limit: 10,
   *   status: 'delivered'
   * });
   * console.debug('Queries:', response.queries);
   * ```
   */
  async getAllQueries(params?: ListQueriesParams): Promise<ListQueriesResponse> {
    const queryParams = params
      ? this.buildQueryString(params as Record<string, string | number | boolean | undefined>)
      : '';
    return this.get<ListQueriesResponse>(`/queries${queryParams}`);
  }

  /**
   * Get a single query by ID
   * GET /queries/{queryId}
   *
   * @param queryId - The unique identifier of the query
   * @returns Promise with the query details
   * @throws {ApiError} If the query is not found
   *
   * @example
   * ```typescript
   * const query = await datasetsApi.getQueryById('query-123');
   * console.debug('Query status:', query.status);
   * ```
   */
  async getQueryById(queryId: string): Promise<DatasetQuery> {
    const raw = await this.get<RawQueryPayload>(`/queries/${queryId}`);
    return mapQueryResponseToDatasetQuery(raw);
  }

  /**
   * Get paginated query results
   * GET /queries/{queryId}/results?limit=N&offset=N
   *
   * @param queryId - The unique identifier of the query
   * @param limit - Maximum number of results to return
   * @param offset - Number of results to skip
   * @returns Promise with paginated results
   * @throws {ApiError} If the query results cannot be fetched
   *
   * @example
   * ```typescript
   * const results = await datasetsApi.getQueryResults('query-123', 20, 0);
   * console.debug('Results:', results.data.length, 'of', results.totalCount);
   * ```
   */
  async getQueryResults(
    queryId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<{
    data: Record<string, unknown>[];
    totalCount: number;
    limit: number;
    offset: number;
    schema: SchemaField[];
  }> {
    const queryParams = this.buildQueryString({ limit: String(limit), offset: String(offset) });
    return this.get(`/queries/${queryId}/results${queryParams}`);
  }

  // ============================================================================
  // Dataset Icon Upload (GW-2020)
  // ============================================================================

  /**
   * Get a presigned URL for uploading a dataset icon
   * POST /datasets/{datasetId}/icon/presigned-url
   *
   * @param datasetId - The unique identifier of the dataset
   * @param params - Upload parameters (filename, contentType)
   * @returns Promise with uploadUrl, cloudfrontUrl, expiresIn, key
   * @throws {ApiError} If presigned URL generation fails
   */
  async getIconPresignedUrl(
    datasetId: string,
    params: { filename: string; contentType: string }
  ): Promise<{ uploadUrl: string; cloudfrontUrl: string; expiresIn: number; key: string }> {
    return this.post(`/datasets/${datasetId}/icon/presigned-url`, params);
  }

  // ============================================================================
  // TOS (Terms of Service) APIs (GW-11440/GW-11453, PRP-MKTPL-016 FR-6/FR-7)
  // ============================================================================
  //
  // Split-endpoint canonical shape, identical field names/semantics to the
  // applications/missions/service-offering TOS contracts (PRP-MKTPL-016's
  // unification design). Replaces the combined GET .../tos/status + POST
  // .../tos/accept endpoints this client previously exposed — breaking
  // change, coordinated with gw-datasets-api's GW-11440 backend migration.

  /**
   * Check TOS acceptance status for a dataset
   * GET /datasets/{datasetId}/tos/acceptance
   *
   * @param datasetId - The unique identifier of the dataset
   * @returns Promise with the canonical TOS acceptance status
   * @throws {ApiError} If the API request fails
   *
   * @example
   * ```typescript
   * const status = await datasetsApi.checkTOSAcceptance('dataset-123');
   * console.debug('Can proceed:', status.canProceed);
   * ```
   */
  async checkTOSAcceptance(datasetId: string): Promise<DatasetTOSAcceptanceStatus> {
    return this.get<DatasetTOSAcceptanceStatus>(`/datasets/${datasetId}/tos/acceptance`);
  }

  /**
   * Accept TOS for a dataset as an individual user
   * POST /datasets/{datasetId}/tos/accept/user
   *
   * @param datasetId - The unique identifier of the dataset
   * @param tosVersion - The dataset's current TOS version being accepted
   * @returns Promise with the recorded acceptance
   * @throws {ApiError} If the acceptance fails
   *
   * @example
   * ```typescript
   * await datasetsApi.acceptTOSUser('dataset-123', 1);
   * ```
   */
  async acceptTOSUser(datasetId: string, tosVersion: number): Promise<DatasetTOSAcceptResponse> {
    return this.post<DatasetTOSAcceptResponse>(`/datasets/${datasetId}/tos/accept/user`, {
      tosVersion,
    });
  }

  /**
   * Accept TOS for a dataset on behalf of an organization (org_admin only)
   * POST /datasets/{datasetId}/tos/accept/org
   *
   * @param datasetId - The unique identifier of the dataset
   * @param tosVersion - The dataset's current TOS version being accepted
   * @returns Promise with the recorded acceptance
   * @throws {ApiError} If the acceptance fails or the caller is not org_admin/global_admin
   *
   * @example
   * ```typescript
   * await datasetsApi.acceptTOSOrg('dataset-123', 1);
   * ```
   */
  async acceptTOSOrg(datasetId: string, tosVersion: number): Promise<DatasetTOSAcceptResponse> {
    return this.post<DatasetTOSAcceptResponse>(`/datasets/${datasetId}/tos/accept/org`, {
      tosVersion,
    });
  }

  /**
   * GW-11493: FR-29 cross-dataset org TOS overview (org_admin/global_admin only).
   * GET /datasets/tos/org-overview
   */
  async getOrgTOSOverview(): Promise<DatasetOrgTOSOverviewResponse> {
    return this.get<DatasetOrgTOSOverviewResponse>('/datasets/tos/org-overview');
  }

  // ============================================================================
  // Audience Access Management (GW-6686)
  // ============================================================================

  /**
   * Add an organization to a dataset's access list
   * POST /v1/datasets/{id}/access
   *
   * @param datasetId - The unique identifier of the dataset
   * @param orgId - The organization ID to grant access to
   * @returns Promise resolving when access is granted
   * @throws {ApiError} If the API request fails
   *
   * @example
   * ```typescript
   * await datasetsApi.addDatasetAccess('dataset-123', 'org-456');
   * ```
   */
  async addDatasetAccess(datasetId: string, orgId: string): Promise<void> {
    return this.post<void>(`/datasets/${datasetId}/access`, { orgId });
  }

  /**
   * Remove an organization from a dataset's access list
   * DELETE /v1/datasets/{id}/access/{orgId}
   *
   * @param datasetId - The unique identifier of the dataset
   * @param orgId - The organization ID to revoke access from
   * @returns Promise resolving when access is revoked
   * @throws {ApiError} If the API request fails
   *
   * @example
   * ```typescript
   * await datasetsApi.removeDatasetAccess('dataset-123', 'org-456');
   * ```
   */
  async removeDatasetAccess(datasetId: string, orgId: string): Promise<void> {
    return this.delete<void>(`/datasets/${datasetId}/access/${orgId}`);
  }

  /**
   * Update dataset grant requirements (for public datasets with grant-based access).
   * PUT /v1/datasets/{id}/audience
   *
   * GW-11062: this MUST target the dedicated audience endpoint (handleSetAudience,
   * which does `SET audience = :a`), NOT the generic PUT /datasets/{id}. The generic
   * update model (UpdateDatasetRequest) has no `audience` field, so it silently
   * ignores any audience payload — meaning both setting AND clearing grants no-op.
   *
   * Passing an empty `grants` array clears the requirement: the backend stores
   * `{type: GRANT_REQUIRED, grants: []}`, and the access-gating loop over required
   * grants becomes a no-op, leaving the dataset effectively unrestricted.
   *
   * @param datasetId - The unique identifier of the dataset
   * @param grants - Array of grant names required to access the dataset (empty = clear)
   * @returns Promise resolving to the updated audience
   * @throws {ApiError} If the API request fails
   *
   * @example
   * ```typescript
   * // Require grants
   * await datasetsApi.updateDatasetGrants('dataset-123', ['privy-1', 'privy-2']);
   * // Clear grant requirement (back to unrestricted)
   * await datasetsApi.updateDatasetGrants('dataset-123', []);
   * ```
   */
  async updateDatasetGrants(
    datasetId: string,
    grants: string[]
  ): Promise<import('@gw/ui/types').Audience> {
    return this.put<import('@gw/ui/types').Audience>(`/datasets/${datasetId}/audience`, {
      type: 'GRANT_REQUIRED',
      grants: grants.map((name) => ({ name })),
    });
  }

  // ============================================================================
  // Query Proxy Config (GW-1798 Phase 2)
  // ============================================================================

  /**
   * Get query proxy configuration including elastic IPs.
   * GET /config/query-proxy
   *
   * Returns the static elastic IP addresses used by the GW query proxy.
   * Publishers need to whitelist these IPs in their API firewall rules.
   *
   * @returns Promise with elasticIps array and environment name
   * @throws {ApiError} If the API request fails
   */
  async getQueryProxyConfig(): Promise<QueryProxyConfig> {
    return this.get('/config/query-proxy');
  }

  // ============================================================================
  // Marketplace Metrics (GW-10584)
  // ============================================================================

  /**
   * Get marketplace usage metrics for a dataset — total query count and most
   * common endpoints over a lookback period. Backs @gw/ui's DatasetUsageCard.
   * GET /marketplace/datasets/{datasetId}/metrics
   *
   * Available to any authenticated platform user, regardless of org — this
   * is a narrower, publicly-readable sibling of the owner-gated
   * /datasets/{id}/metrics endpoint.
   *
   * @param datasetId - The unique identifier of the dataset
   * @param period - Lookback window ('7d' | '30d' | '90d')
   * @throws {ApiError} If the API request fails
   */
  async getMarketplaceMetrics(
    datasetId: string,
    period: DatasetUsagePeriod
  ): Promise<{ totalQueries: number; commonEndpoints: DatasetUsageCommonEndpoint[] }> {
    const queryParams = this.buildQueryString({ period });
    return this.get(`/marketplace/datasets/${datasetId}/metrics${queryParams}`);
  }
}

// Export singleton instance
export const datasetsApi = new DatasetsApiClient();
